import {
  BankOutlined,
  CheckCircleFilled,
  CloseOutlined,
  GlobalOutlined,
  PlusOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { InputRef } from 'antd';
import { Input, message, Select } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Organization } from '../../api/organization';
import { inviteUsersToOrganization } from '../../api/user';
import { LogoFullIcon } from '../../assets/CustomIcons/LogoFullIcon';
import UnionBg from '../../assets/icons/union-bg.svg';
import { getCurrentlyActiveOrganization, OrganizationAccessRoles, useGetUserFromAccessToken } from '../../common/auth';
import { extractDomain, isCompanyEmail, isTemporaryEmail, isValidEmail } from '../../common/utils';
import { BaseButton } from '../../components/UI/BaseButton';
import CustomTooltip from '../../components/UI/Tooltip/CustomTooltip';
import RoutePaths from '../../router/RoutePaths';

type ErrorSeverity = 'error' | 'warning';

interface ValidationError {
  message: string;
  severity: ErrorSeverity;
}

interface InviteRow {
  id: string;
  email: string;
  role: OrganizationAccessRoles;
  error: ValidationError | null;
}

export default function NewOrgInvite() {
  const navigate = useNavigate();
  const currentUser = useGetUserFromAccessToken();
  const [activeOrg, setActiveOrg] = useState<Organization | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const firstInputRef = useRef<InputRef>(null);
  const [focusRowId, setFocusRowId] = useState<string | null>(null);

  // Initialize with 5 empty rows
  const [inviteRows, setInviteRows] = useState<InviteRow[]>([
    { id: '1', email: '', role: OrganizationAccessRoles.MEMBER, error: null },
    { id: '2', email: '', role: OrganizationAccessRoles.MEMBER, error: null },
    { id: '3', email: '', role: OrganizationAccessRoles.MEMBER, error: null },
    { id: '4', email: '', role: OrganizationAccessRoles.MEMBER, error: null },
    { id: '5', email: '', role: OrganizationAccessRoles.MEMBER, error: null },
  ]);

  // Get active organization on mount
  useEffect(() => {
    const getOrg = async () => {
      const org = await getCurrentlyActiveOrganization();
      setActiveOrg(org);
    };
    void getOrg();
  }, []);

  // Auto-focus first input on mount
  useEffect(() => {
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, []);

  // Auto-focus newly added row
  useEffect(() => {
    if (focusRowId) {
      const input = document.querySelector(`input[data-row-id="${focusRowId}"]`) as HTMLInputElement;
      if (input) {
        input.focus();
        setFocusRowId(null);
      }
    }
  }, [focusRowId, inviteRows]);

  const validateEmail = (email: string, role: OrganizationAccessRoles): ValidationError | null => {
    if (!email) return null; // Empty is valid (optional field)

    if (!isValidEmail(email)) {
      return {
        message: 'Invalid email',
        severity: 'error',
      };
    }

    if (isTemporaryEmail(email)) {
      return {
        message: 'Emails from temporary public domains are not allowed',
        severity: 'error',
      };
    }

    // Email already in current invitation flow
    const allEmails = inviteRows.map((row) => row.email);
    const duplicates = allEmails.filter((e) => e === email);
    if (duplicates.length > 1) {
      return {
        message: 'This email is already included in the current organization invitation',
        severity: 'warning',
      };
    }

    return null;
  };

  const handleEmailChange = (rowId: string, newEmail: string) => {
    setInviteRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id === rowId) {
          return { ...row, email: newEmail, error: null };
        }
        return row;
      })
    );
  };

  const handleEmailBlur = (rowId: string) => {
    setInviteRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id === rowId) {
          const error = validateEmail(row.email, row.role);
          return { ...row, error };
        }
        return row;
      })
    );
  };

  const handleEmailKeyDown = (rowId: string, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      // Prevent space from being added to input
      if (e.key === ' ') {
        e.preventDefault();
      }

      setInviteRows((prevRows) =>
        prevRows.map((row) => {
          if (row.id === rowId) {
            const error = validateEmail(row.email, row.role);
            return { ...row, error };
          }
          return row;
        })
      );
    }
  };

  const handleRoleChange = (rowId: string, newRole: OrganizationAccessRoles) => {
    setInviteRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id === rowId) {
          // Re-validate with new role since validation rules differ for Member vs Admin
          const error = row.email ? validateEmail(row.email, newRole) : null;
          return { ...row, role: newRole, error };
        }
        return row;
      })
    );
  };

  const handleDeleteRow = (rowId: string) => {
    setInviteRows((prevRows) => prevRows.filter((row) => row.id !== rowId));
  };

  const handleAddRow = () => {
    const newRow: InviteRow = {
      id: Date.now().toString(),
      email: '',
      role: OrganizationAccessRoles.MEMBER,
      error: null,
    };
    setInviteRows((prevRows) => [...prevRows, newRow]);
    setFocusRowId(newRow.id);
  };

  const getEmailIcon = (email: string, error: ValidationError | null) => {
    // If there's an error, show error icon
    if (error) {
      if (error.severity === 'error') {
        return <WarningOutlined className="text-red-600" />;
      } else {
        return <WarningOutlined className="text-yellow-600" />;
      }
    }

    // If no error and email exists, show email type icon
    if (email && isValidEmail(email)) {
      const currentUserEmail = currentUser.data?.user.email;
      const isOrgDomain =
        currentUserEmail && isCompanyEmail(currentUserEmail)
          ? extractDomain(currentUserEmail) === extractDomain(email)
          : false;

      if (isOrgDomain) {
        return <BankOutlined className="text-neutral-600" />;
      } else {
        return <GlobalOutlined className="text-neutral-600" />;
      }
    }

    return null;
  };

  const handleInvite = async () => {
    const validInvites = inviteRows.filter((row) => row.email && !row.error);

    if (validInvites.length === 0) return;

    setIsSubmitting(true);
    try {
      const users = validInvites.map((row) => ({
        email: row.email,
        role: row.role,
      }));
      await inviteUsersToOrganization(users);

      void message.success('Invitations sent successfully');

      // Navigate to new space creation
      if (activeOrg) {
        navigate(RoutePaths.Organization.children.NewSpace.replace(':orgId', activeOrg.publicId.toString()));
      }
    } catch (error: any) {
      const errorCode = error?.response?.data?.code;
      const errorMessage = error?.response?.data?.message;

      if (errorCode === 'ERR-USER-33') {
        void message.error('Only admins can invite other admins');
      } else if (errorCode === 'ERR-USER-34') {
        void message.error('Please provide valid user information');
      } else if (errorCode === 'ERR-USER-35') {
        void message.error('Invalid role specified');
      } else {
        void message.error(errorMessage || 'Failed to invite users');
      }
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    // Navigate to new space creation
    if (activeOrg) {
      navigate(RoutePaths.Organization.children.NewSpace.replace(':orgId', activeOrg.publicId.toString()));
    }
  };

  // Check if invite button should be disabled
  const hasRedErrors = inviteRows.some((row) => row.error?.severity === 'error');
  const hasAtLeastOneValidEmail = inviteRows.some((row) => row.email && !row.error);
  const isInviteDisabled = hasRedErrors || !hasAtLeastOneValidEmail;

  return (
    <div
      className="flex justify-center items-center min-h-screen bg-brand-white"
      style={{
        backgroundImage: `url(${UnionBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="flex flex-col w-[656px] h-[80vh] bg-brand-white border-2 border-solid border-taskonioGreen-600 rounded-[12px] py-6 px-10 relative shadow-[0_0_0_4px_#7ff5aa,0_4px_16px_0_rgba(112,119,146,0.4)]">
        <div className="flex flex-col items-center justify-center gap-5 my-10">
          <LogoFullIcon />
          <div className="flex items-center justify-center gap-2 font-semibold text-base">
            <CheckCircleFilled className="text-taskonioGreen-600" />
            {activeOrg?.name || 'Organization'} successfully created
          </div>
        </div>

        <h1 className="font-semibold text-xl text-brand-grey-dark mr-auto mb-5">
          Add your teammates to your Organization
        </h1>

        <div className="flex flex-col gap-2 mb-2 overflow-y-auto flex-1 min-h-0 pr-2">
          {inviteRows.map((row, index) => (
            <div key={row.id} className="flex flex-col gap-[2px]">
              <div className="flex gap-3 items-center">
                <div className="flex-1 relative">
                  <Input
                    ref={index === 0 ? firstInputRef : null}
                    className="h-10 border-neutral-500 text-neutral-800 text-base font-normal pr-10"
                    placeholder="Enter email"
                    value={row.email}
                    onChange={(e) => handleEmailChange(row.id, e.target.value)}
                    onBlur={() => handleEmailBlur(row.id)}
                    onKeyDown={(e) => handleEmailKeyDown(row.id, e)}
                    status={row.error?.severity === 'error' ? 'error' : ''}
                    size="large"
                    data-row-id={row.id}
                  />
                  {getEmailIcon(row.email, row.error) && (
                    <CustomTooltip
                      title={
                        row.error?.message ||
                        (row.email && isValidEmail(row.email)
                          ? isCompanyEmail(currentUser.data?.user.email || '') &&
                            extractDomain(currentUser.data?.user.email || '') === extractDomain(row.email)
                            ? 'Organization domain email'
                            : 'External domain email'
                          : '')
                      }
                    >
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 cursor-default">
                        {getEmailIcon(row.email, row.error)}
                      </div>
                    </CustomTooltip>
                  )}
                </div>
                <Select
                  className="h-8 ml-1 w-[90px] text-right"
                  value={row.role}
                  onChange={(value) => handleRoleChange(row.id, value)}
                  size="small"
                  bordered={false}
                  popupClassName="[&_.ant-select-item]:text-right"
                >
                  <Select.Option value={OrganizationAccessRoles.MEMBER}>Member</Select.Option>
                  <Select.Option value={OrganizationAccessRoles.ADMIN}>Admin</Select.Option>
                </Select>
                <CustomTooltip title={inviteRows.length === 1 ? 'At least one field is required' : 'Remove email'}>
                  <CloseOutlined
                    className={`flex items-center justify-center w-5 h-5 p-1 rounded-sm ${
                      inviteRows.length === 1
                        ? 'text-neutral-500 cursor-not-allowed'
                        : 'text-neutral-900 cursor-pointer hover:bg-neutral-200'
                    }`}
                    onClick={() => inviteRows.length > 1 && handleDeleteRow(row.id)}
                  />
                </CustomTooltip>
              </div>
            </div>
          ))}
          <div className="flex justify-center">
            <BaseButton type="secondary" onClick={handleAddRow} size="medium">
              <PlusOutlined className="text-base" />
              Add Another Member
            </BaseButton>
            <div className="ml-auto"></div>
          </div>
        </div>

        <div className="flex justify-between gap-2 mt-4">
          <BaseButton type="secondary" size="large" onClick={handleSkip}>
            Skip for now
          </BaseButton>
          <BaseButton type="primary" size="large" onClick={handleInvite} disabled={isInviteDisabled || isSubmitting}>
            {isSubmitting ? 'Inviting...' : 'Invite to Organization'}
          </BaseButton>
        </div>
      </div>
    </div>
  );
}
